import { useClerk } from '@clerk/react-router';
import { hasProperty } from '@plato/shared-util/object';
import { captureException } from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchStreamLink, httpLink, loggerLink, splitLink, type HTTPHeaders } from '@trpc/client';
import { useState, type ReactElement, type ReactNode } from 'react';
import superjson from 'superjson';
import { api } from '../../utils/api.ts';

export type TRPCProviderProps = {
    children: ReactNode;
};

export function TRPCProvider({ children }: TRPCProviderProps): ReactElement {
    const clerk = useClerk();

    const [queryClient] = useState(() => {
        return new QueryClient({
            queryCache: new QueryCache({
                onError: (error, query) => {
                    const [path, options] = query.queryKey;

                    captureException(error, {
                        contexts: {
                            trpc: {
                                path: Array.isArray(path) ? path.join('.') : String(path),
                                input: hasProperty(options, 'input') ? options.input : undefined,
                            },
                        },
                    });
                },
            }),
            mutationCache: new MutationCache({
                onError: (error, variables, _context, mutation) => {
                    captureException(error, {
                        contexts: {
                            trpc: {
                                path: mutation.options.mutationKey?.flat().join('.'),
                                input: variables,
                            },
                        },
                    });
                },
            }),
            defaultOptions: {
                queries: { refetchOnWindowFocus: false },
            },
        });
    });

    const [trpcClient] = useState(() => {
        function headers(): HTTPHeaders {
            if (clerk.session) {
                // add the rate limit key
                return { 'x-clerk-user-id': clerk.session.user.id };
            }
            return {};
        }

        return api.createClient({
            links: [
                loggerLink({
                    enabled: (opts) =>
                        import.meta.env.DEV || (opts.direction === 'down' && opts.result instanceof Error),
                }),
                splitLink({
                    // platoAiChat procedures stream async-generator responses,
                    // which only work over httpBatchStreamLink.
                    condition: (op) => op.path.startsWith('platoAiChat.'),
                    true: httpBatchStreamLink({
                        transformer: superjson,
                        url: '/api/trpc',
                        headers,
                    }),
                    false: httpLink({
                        transformer: superjson,
                        url: '/api/trpc',
                        headers,
                    }),
                }),
            ],
        });
    });

    return (
        <api.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </api.Provider>
    );
}
